import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, HeadingLevel, AlignmentType, ImageRun } from 'docx';

interface ExportOptions {
  content: string;
  clientName: string;
  firmName?: string;
  timestamp?: Date;
  messageElement?: HTMLElement | null;
}

/**
 * Capture all chart SVGs from a message element as PNG images
 */
async function captureCharts(messageElement: HTMLElement | null): Promise<Array<{ index: number; dataUrl: string; width: number; height: number }>> {
  if (!messageElement) return [];
  const charts: Array<{ index: number; dataUrl: string; width: number; height: number }> = [];

  // Find all recharts containers
  const chartContainers = messageElement.querySelectorAll('.recharts-responsive-container');

  for (let i = 0; i < chartContainers.length; i++) {
    const container = chartContainers[i] as HTMLElement;
    const svg = container.querySelector('svg');
    if (!svg) continue;

    try {
      // Clone SVG and set explicit dimensions
      const svgClone = svg.cloneNode(true) as SVGElement;
      const rect = svg.getBoundingClientRect();
      const width = Math.round(rect.width) || 500;
      const height = Math.round(rect.height) || 300;

      svgClone.setAttribute('width', String(width));
      svgClone.setAttribute('height', String(height));
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

      // Inline all computed styles
      const allElements = svgClone.querySelectorAll('*');
      const originalElements = svg.querySelectorAll('*');
      for (let j = 0; j < allElements.length && j < originalElements.length; j++) {
        const computed = window.getComputedStyle(originalElements[j]);
        const el = allElements[j] as SVGElement;
        // Copy key styles
        for (const prop of ['fill', 'stroke', 'stroke-width', 'opacity', 'font-size', 'font-family', 'text-anchor', 'dominant-baseline']) {
          const val = computed.getPropertyValue(prop);
          if (val && val !== 'none' && val !== 'normal' && val !== '') {
            el.style.setProperty(prop, val);
          }
        }
      }

      // Add background matching export style (always white for export/print)
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('width', '100%');
      bgRect.setAttribute('height', '100%');
      bgRect.setAttribute('fill', '#ffffff');
      svgClone.insertBefore(bgRect, svgClone.firstChild);

      // Fix text colors for light background export
      svgClone.querySelectorAll('text, tspan').forEach(el => {
        const currentFill = (el as SVGElement).style.fill || el.getAttribute('fill') || '';
        // If text is light colored (for dark theme), make it dark for export
        if (!currentFill || currentFill === 'none' || currentFill === '#888' || currentFill === '#999' || currentFill === '#e2e8f0' || currentFill === '#94a3b8' || currentFill.includes('rgb(255') || currentFill.includes('rgb(226') || currentFill.includes('rgb(148')) {
          (el as SVGElement).style.fill = '#374151';
        }
      });

      // Fix grid lines for light background
      svgClone.querySelectorAll('line').forEach(el => {
        const stroke = (el as SVGElement).style.stroke || el.getAttribute('stroke') || '';
        if (stroke.includes('rgb(255') || stroke.includes('#fff') || !stroke) {
          (el as SVGElement).style.stroke = '#e5e7eb';
        }
      });

      // Convert to data URL
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      // Draw on canvas
      const canvas = document.createElement('canvas');
      canvas.width = width * 2; // 2x for retina
      canvas.height = height * 2;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(2, 2);

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load chart')); };
        img.src = url;
      });

      const dataUrl = canvas.toDataURL('image/png');
      charts.push({ index: i, dataUrl, width, height });
    } catch (e) {
      console.error('Failed to capture chart:', e);
    }
  }

  return charts;
}

/**
 * Convert data URL to ArrayBuffer for docx ImageRun
 */
function dataUrlToBuffer(dataUrl: string): ArrayBuffer {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

// ==================== PDF Export ====================

export async function exportToPdf({ content, clientName, timestamp, messageElement }: ExportOptions) {
  const html2pdf = (await import('html2pdf.js')).default;

  // Capture charts before building HTML
  const charts = await captureCharts(messageElement || null);

  // Replace [CHART] blocks with chart images
  let htmlContent = markdownToHtml(content);
  if (charts.length > 0) {
    let chartIdx = 0;
    htmlContent = htmlContent.replace(/\[Chart \d+\]/g, () => {
      const chart = charts[chartIdx++];
      if (chart) {
        return `<img src="${chart.dataUrl}" style="width:100%;max-width:${chart.width}px;height:auto;margin:12px 0;border-radius:8px;" />`;
      }
      return '[Chart]';
    });
  }

  // Create a visible element temporarily (html2pdf needs it in the DOM and visible)
  const wrapper = document.createElement('div');
  wrapper.id = '__pdf_export_wrapper__';
  wrapper.innerHTML = `
    <div style="font-family: Segoe UI, Arial, sans-serif; padding: 40px; color: #1a1a1a; max-width: 780px; background: white;">
      <div style="border-bottom: 3px solid #2563eb; padding-bottom: 16px; margin-bottom: 24px;">
        <h1 style="margin: 0; font-size: 20px; color: #2563eb;">${clientName}</h1>
        <p style="margin: 4px 0 0; font-size: 12px; color: #666;">
          Generated: ${(timestamp || new Date()).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      <div style="font-size: 13px; line-height: 1.7;">${htmlContent}</div>
      <div style="margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #999; text-align: center;">
        Generated by RoniDoc AI
      </div>
    </div>
  `;
  // Position off-screen but still rendered (not display:none)
  wrapper.style.cssText = 'position:fixed;top:0;left:0;width:800px;z-index:99999;opacity:0;pointer-events:none;';
  document.body.appendChild(wrapper);

  await new Promise(r => setTimeout(r, 100));

  try {
    await html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_report.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(wrapper.firstElementChild)
      .save();
  } finally {
    document.body.removeChild(wrapper);
  }
}

// ==================== Word Export ====================

export async function exportToWord({ content, clientName, timestamp, messageElement }: ExportOptions) {
  // Capture charts
  const charts = await captureCharts(messageElement || null);
  let chartIdx = 0;

  const lines = content.split('\n');
  const children: (Paragraph | Table)[] = [];

  // Title
  children.push(new Paragraph({
    children: [new TextRun({ text: clientName, bold: true, size: 32, color: '2563eb' })],
    heading: HeadingLevel.HEADING_1,
    spacing: { after: 100 },
  }));

  // Date
  children.push(new Paragraph({
    children: [new TextRun({
      text: `Generated: ${(timestamp || new Date()).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`,
      size: 20, color: '666666', italics: true,
    })],
    spacing: { after: 300 },
  }));

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Table detection
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        // Skip separator rows
        if (!/^\|[\s\-:|]+\|$/.test(lines[i].trim())) {
          tableLines.push(lines[i]);
        }
        i++;
      }
      if (tableLines.length > 0) {
        children.push(createWordTable(tableLines));
        children.push(new Paragraph({ spacing: { after: 200 } }));
      }
      continue;
    }

    // Insert chart image or skip [CHART] blocks
    if (line.includes('[CHART]')) {
      while (i < lines.length && !lines[i].includes('[/CHART]')) i++;
      i++;
      // Insert chart image if captured
      const chart = charts[chartIdx++];
      if (chart) {
        try {
          const imgBuffer = dataUrlToBuffer(chart.dataUrl);
          const maxWidth = 550;
          const scale = Math.min(1, maxWidth / chart.width);
          children.push(new Paragraph({
            children: [new ImageRun({
              data: imgBuffer,
              transformation: { width: Math.round(chart.width * scale), height: Math.round(chart.height * scale) },
              type: 'png',
            })],
            spacing: { before: 200, after: 200 },
          }));
        } catch (e) {
          console.error('Failed to insert chart in Word:', e);
        }
      }
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      children.push(new Paragraph({
        children: [new TextRun({ text: line.replace(/^###\s*/, ''), bold: true, size: 24 })],
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 100 },
      }));
    } else if (line.startsWith('## ')) {
      children.push(new Paragraph({
        children: [new TextRun({ text: line.replace(/^##\s*/, ''), bold: true, size: 28 })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 100 },
      }));
    } else if (line.startsWith('# ')) {
      children.push(new Paragraph({
        children: [new TextRun({ text: line.replace(/^#\s*/, ''), bold: true, size: 32 })],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 300, after: 100 },
      }));
    } else if (line.trim() === '') {
      children.push(new Paragraph({ spacing: { after: 100 } }));
    } else {
      // Regular text with bold/italic support
      children.push(new Paragraph({
        children: parseInlineFormatting(line.replace(/^[*-]\s+/, '')),
        spacing: { after: 60 },
        bullet: /^[*-]\s+/.test(line) ? { level: 0 } : undefined,
      }));
    }
    i++;
  }

  // Footer
  children.push(new Paragraph({
    children: [new TextRun({ text: 'Generated by RoniDoc AI', size: 16, color: '999999', italics: true })],
    alignment: AlignmentType.CENTER,
    spacing: { before: 400 },
  }));

  const doc = new Document({
    sections: [{ properties: {}, children }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_report.docx`);
}

function createWordTable(tableLines: string[]): Table {
  const rows = tableLines.map((line, rowIdx) => {
    const cells = line.split('|').filter(c => c.trim() !== '').map(c => c.trim());
    return new TableRow({
      children: cells.map(cellText => new TableCell({
        children: [new Paragraph({
          children: rowIdx === 0
            ? [new TextRun({ text: cellText.replace(/\*\*/g, ''), bold: true, size: 20 })]
            : parseInlineFormatting(cellText),
          spacing: { before: 40, after: 40 },
        })],
        width: { size: Math.floor(10000 / cells.length), type: WidthType.DXA },
        shading: rowIdx === 0 ? { fill: 'e8f0fe' } : undefined,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' },
          left: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' },
          right: { style: BorderStyle.SINGLE, size: 1, color: 'cccccc' },
        },
      })),
    });
  });

  return new Table({ rows, width: { size: 9000, type: WidthType.DXA } });
}

function parseInlineFormatting(text: string): TextRun[] {
  const runs: TextRun[] = [];
  // Match **bold**, *italic*, `code`, or plain text (non-greedy, handles multiword)
  const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|([^*`]+)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match[1]) {
      runs.push(new TextRun({ text: match[1], bold: true, size: 22 }));
    } else if (match[2]) {
      runs.push(new TextRun({ text: match[2], italics: true, size: 22 }));
    } else if (match[3]) {
      runs.push(new TextRun({ text: match[3], font: 'Courier New', size: 20 }));
    } else if (match[4]) {
      runs.push(new TextRun({ text: match[4], size: 22 }));
    }
  }
  if (runs.length === 0) {
    // Fallback: strip any remaining ** and output plain
    runs.push(new TextRun({ text: text.replace(/\*\*/g, ''), size: 22 }));
  }
  return runs;
}

// ==================== CSV Export ====================

export function exportToCsv({ content, clientName }: ExportOptions) {
  // Extract all tables from the markdown
  const lines = content.split('\n');
  const csvSections: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Detect table start
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        // Skip separator rows
        if (!/^\|[\s\-:|]+\|$/.test(lines[i].trim())) {
          const cells = lines[i].split('|').filter(c => c.trim() !== '').map(c => c.trim());
          tableRows.push(cells);
        }
        i++;
      }
      if (tableRows.length > 0) {
        const csv = tableRows.map(row =>
          row.map(cell => `"${cell.replace(/\*\*/g, '').replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        csvSections.push(csv);
      }
      continue;
    }
    i++;
  }

  if (csvSections.length === 0) {
    // No tables found — export the raw text with markdown stripped
    const cleaned = content.replace(/\*\*/g, '').replace(/\*/g, '');
    const blob = new Blob(['\uFEFF' + cleaned], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_export.csv`);
    return;
  }

  const fullCsv = csvSections.join('\n\n');
  const blob = new Blob(['\uFEFF' + fullCsv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel
  saveAs(blob, `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_export.csv`);
}

// ==================== Excel Export ====================

export async function exportToExcel({ content, clientName, timestamp }: ExportOptions) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'RoniDoc AI';
  workbook.created = timestamp || new Date();

  const lines = content.split('\n');
  let currentSheet = workbook.addWorksheet('Report');
  let sheetRow = 1;
  let tableCount = 0;

  // Add header
  const titleRow = currentSheet.getRow(sheetRow);
  titleRow.getCell(1).value = clientName;
  titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: '2563EB' } };
  sheetRow++;
  const dateRow = currentSheet.getRow(sheetRow);
  dateRow.getCell(1).value = `Generated: ${(timestamp || new Date()).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  dateRow.getCell(1).font = { italic: true, size: 10, color: { argb: '666666' } };
  sheetRow += 2;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Skip [CHART] blocks in Excel
    if (line.includes('[CHART]')) {
      while (i < lines.length && !lines[i].includes('[/CHART]')) i++;
      i++;
      continue;
    }

    // Detect tables — put each table on its own sheet if there are multiple
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines: string[] = [];
      // Get heading before table
      let tableTitle = '';
      for (let j = i - 1; j >= 0; j--) {
        const prev = lines[j].trim();
        if (prev.startsWith('#')) { tableTitle = prev.replace(/^#+\s*/, '').replace(/\*\*/g, ''); break; }
        if (prev === '') continue;
        if (!prev.startsWith('|')) { tableTitle = prev.replace(/\*\*/g, ''); break; }
      }

      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        if (!/^\|[\s\-:|]+\|$/.test(lines[i].trim())) {
          tableLines.push(lines[i]);
        }
        i++;
      }

      if (tableLines.length > 0) {
        tableCount++;
        // Create new sheet for each table (after the first)
        if (tableCount > 1) {
          const sheetName = (tableTitle || `Table ${tableCount}`).substring(0, 31);
          currentSheet = workbook.addWorksheet(sheetName);
          sheetRow = 1;
        }

        // Write table rows
        tableLines.forEach((tl, rowIdx) => {
          const cells = tl.split('|').filter(c => c.trim() !== '').map(c => c.trim().replace(/\*\*/g, ''));
          const row = currentSheet.getRow(sheetRow);

          cells.forEach((cellText, colIdx) => {
            const cell = row.getCell(colIdx + 1);
            // Try to parse as number
            const numVal = parseFloat(cellText.replace(/[$,\s]/g, ''));
            if (!isNaN(numVal) && cellText.match(/[\d.]+/)) {
              cell.value = numVal;
              if (cellText.includes('$')) {
                cell.numFmt = '$#,##0.00';
              } else if (cellText.includes('%')) {
                cell.value = numVal / 100;
                cell.numFmt = '0.0%';
              } else {
                cell.numFmt = '#,##0.##';
              }
            } else {
              cell.value = cellText;
            }

            // Header row styling
            if (rowIdx === 0) {
              cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2563EB' } };
              cell.alignment = { horizontal: 'center' };
            } else {
              cell.font = { size: 10 };
              cell.alignment = { horizontal: cellText.match(/^[\$\d\-\(]/) ? 'right' : 'left' };
            }
            cell.border = {
              top: { style: 'thin', color: { argb: 'D1D5DB' } },
              bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
              left: { style: 'thin', color: { argb: 'D1D5DB' } },
              right: { style: 'thin', color: { argb: 'D1D5DB' } },
            };
          });

          // Alternate row colors
          if (rowIdx > 0 && rowIdx % 2 === 0) {
            cells.forEach((_, colIdx) => {
              row.getCell(colIdx + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
            });
          }

          sheetRow++;
        });

        // Auto-fit column widths
        currentSheet.columns.forEach(col => {
          let maxLen = 10;
          col.eachCell?.({ includeEmpty: false }, cell => {
            const len = String(cell.value || '').length;
            if (len > maxLen) maxLen = len;
          });
          col.width = Math.min(maxLen + 4, 40);
        });

        sheetRow++;
      }
      continue;
    }

    // Headings
    if (line.startsWith('#')) {
      const text = line.replace(/^#+\s*/, '').replace(/\*\*/g, '');
      const level = line.match(/^(#+)/)?.[1].length || 1;
      const row = currentSheet.getRow(sheetRow);
      row.getCell(1).value = text;
      row.getCell(1).font = { bold: true, size: level === 1 ? 14 : level === 2 ? 12 : 11 };
      sheetRow++;
    } else if (line.trim() !== '' && !line.startsWith('|')) {
      // Regular text
      const text = line.replace(/\*\*/g, '').replace(/^\s*[-*]\s+/, '');
      if (text.trim()) {
        const row = currentSheet.getRow(sheetRow);
        row.getCell(1).value = text;
        row.getCell(1).font = { size: 10 };
        sheetRow++;
      }
    } else if (line.trim() === '') {
      sheetRow++;
    }
    i++;
  }

  // Generate and save
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_report.xlsx`);
}

// ==================== Helpers ====================

function markdownToHtml(md: string): string {
  let html = md;

  // Replace [CHART] blocks with numbered placeholders (matched by PDF exporter for image injection)
  let chartCount = 0;
  html = html.replace(/(?:```(?:json)?\s*)?\[CHART\]\s*[\s\S]*?\s*\[\/CHART\](?:\s*```)?/g, () => {
    chartCount++;
    return `<p><em>[Chart ${chartCount}]</em></p>`;
  });

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h3 style="font-size:15px;font-weight:600;margin:16px 0 8px;color:#1a1a1a;">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 style="font-size:17px;font-weight:600;margin:20px 0 8px;color:#1a1a1a;">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 style="font-size:20px;font-weight:700;margin:24px 0 8px;color:#1a1a1a;">$1</h1>');

  // Bold & italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Tables
  html = html.replace(/^(\|.+\|)\n(\|[\s\-:|]+\|)\n((?:\|.+\|\n?)+)/gm, (_match, header: string, _sep: string, body: string) => {
    const headerCells = header.split('|').filter((c: string) => c.trim()).map((c: string) =>
      `<th style="padding:6px 10px;background:#f0f4ff;border:1px solid #d1d5db;font-weight:600;text-align:left;font-size:12px;">${c.trim()}</th>`
    ).join('');
    const bodyRows = body.trim().split('\n').map((row: string) => {
      const cells = row.split('|').filter((c: string) => c.trim()).map((c: string) =>
        `<td style="padding:5px 10px;border:1px solid #d1d5db;font-size:12px;">${c.trim()}</td>`
      ).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table style="border-collapse:collapse;width:100%;margin:12px 0;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
  });

  // Lists
  html = html.replace(/^[*-] (.+)$/gm, '<li style="margin:2px 0;font-size:13px;">$1</li>');
  html = html.replace(/(<li[^>]*>.*<\/li>\n?)+/g, '<ul style="margin:8px 0;padding-left:20px;">$&</ul>');

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p style="margin:8px 0;">');
  html = html.replace(/\n/g, '<br>');

  return html;
}
